import { useState, useEffect, createContext, useContext, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store';
import { setMobileTab } from '../store/slices/uiSlice';
import { syncPendingDispatch, respondToDispatch, cancelDispatch, completeDispatch, clearActiveDispatch } from '../store/slices/dispatchSlice';
import type { MdtDispatch } from '../types';
import { Home, MessageSquare, Map, FileText } from 'lucide-react';
import { cn, API_BASE_URL, SOCKET_URL } from '../lib/utils';
import { latestPendingDispatch, alertIncomingDispatch } from '../lib/pendingDispatch';
import { logoutMdt } from '../lib/logoutMdt';
import { getMdtEventId } from '../lib/mdtSessionStorage';
import { buildDispatchFromResourceAssignment, dispatchListCoversIncident } from '../lib/restoreMdtAssignment';
import { io } from 'socket.io-client';
import MdtDispatchAlert from '../components/mobile/MdtDispatchAlert';
import MdtMenu from '../components/mobile/MdtMenu';
import OfflineBanner from '../components/mobile/OfflineBanner';
import type { Resource } from '../types';
import { useOfflineSync } from '../hooks/useOfflineSync';
import { useMdtSession } from '../hooks/useMdtSession';

type UnitStatus = 'available' | 'busy' | 'enroute' | 'onscene' | 'onbreak' | 'conveying';

interface MobileContextType {
  unitStatus: UnitStatus;
  setUnitStatus: (s: UnitStatus) => void;
  chatOpen: boolean;
  setChatOpen: (v: boolean) => void;
  mdtResource: Resource | null;
}

const MobileContext = createContext<MobileContextType>({
  unitStatus: 'available',
  setUnitStatus: () => {},
  chatOpen: false,
  setChatOpen: () => {},
  mdtResource: null,
});

export const useMobileContext = () => useContext(MobileContext);

const statusGlow: Record<UnitStatus, string> = {
  available: '#44edaa',
  busy: '#ffb454',
  enroute: '#3de8e0',
  conveying: '#fb923c',
  onscene: '#ff5074',
  onbreak: '#b88cff',
};

const tabs = [
  { id: 'dispatch' as const, icon: Home, label: 'Home', path: '/' },
  { id: 'chat' as const, icon: MessageSquare, label: 'Chat', path: '/chat' },
  { id: 'map' as const, icon: Map, label: 'Map', path: '/map' },
  { id: 'incident' as const, icon: FileText, label: 'Incident', path: '/incident' },
];

interface MobileLayoutProps {
  children: ReactNode;
  mdtResource?: Resource | null;
  sessionReady?: boolean;
}

export default function MobileLayout({ children, mdtResource = null, sessionReady = false }: MobileLayoutProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAppSelector((state) => state.auth);
  useAppSelector((state) => state.ui);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [unitStatus, setUnitStatus] = useState<UnitStatus>('available');
  const [chatOpen, setChatOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const handleLogoutConfirm = async () => {
    setLoggingOut(true);
    try {
      await logoutMdt(dispatch, navigate, token);
    } finally {
      setLoggingOut(false);
    }
  };

  useMdtSession({ mdtResource, unitStatus, sessionReady });

  // Force logout when Command disconnects this unit or claim is revoked
  useEffect(() => {
    if (!sessionReady || !mdtResource || !token) return;

    const onForceLogout = () => {
      void logoutMdt(dispatch, navigate, token);
    };

    window.addEventListener('mdt:force_logout', onForceLogout);
    return () => window.removeEventListener('mdt:force_logout', onForceLogout);
  }, [sessionReady, mdtResource?.id, token, dispatch, navigate]);

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Poll + socket: keep pending dispatches active until MDT accepts/rejects
  useEffect(() => {
    if (!sessionReady || !mdtResource || !token) return;
    const sessionEventId = getMdtEventId(mdtResource);
    const alertedIds = new Set<string>();

    // Always show pending dispatches for this unit; scope accepted/completed by event
    const filterForSession = (list: MdtDispatch[]) =>
      list.filter((d) => {
        if (d.targetUnitId !== mdtResource.id) return false;
        if (d.status === 'pending') return true;
        if (!sessionEventId) return true;
        return !d.eventId || d.eventId === sessionEventId;
      });

    const processList = (list: MdtDispatch[], resourceAssignedIncident?: string | null) => {
      const scoped = filterForSession(list);
      const pending = latestPendingDispatch(scoped);
      if (pending) {
        dispatch(syncPendingDispatch(pending));
        if (!alertedIds.has(pending.id)) {
          alertedIds.add(pending.id);
          alertIncomingDispatch(pending);
        }
      }

      scoped.forEach((d) => {
        if (d.status === 'cancelled') {
          dispatch(cancelDispatch(d.id));
        } else if (d.status === 'completed') {
          dispatch(completeDispatch(d.id));
        } else if (d.status === 'accepted' && d.respondedAt && d.targetUnitId === mdtResource.id) {
          // Restore unless the server explicitly has this unit on a different incident
          const assignedElsewhere =
            resourceAssignedIncident != null && resourceAssignedIncident !== d.incidentId;
          if (!assignedElsewhere) {
            dispatch(syncPendingDispatch(d));
            dispatch(respondToDispatch({ id: d.id, status: d.status, respondedAt: d.respondedAt }));
          } else {
            dispatch(completeDispatch(d.id));
          }
        } else if (d.status === 'rejected' && d.respondedAt) {
          dispatch(cancelDispatch(d.id));
        }
      });
    };

    const pollHeaders = { Authorization: `Bearer ${token}` };
    const liveFetch = { headers: pollHeaders, cache: 'no-store' as RequestCache };

    const poll = async () => {
      try {
        let resourceAssignedIncident: string | null | undefined = mdtResource.assignedIncident ?? null;
        let liveResource: Resource | undefined;

        const params = new URLSearchParams({ targetUnitId: mdtResource.id });
        const res = await fetch(`${API_BASE_URL}/dispatches?${params.toString()}`, liveFetch);
        if (!res.ok) return;
        const data = await res.json();
        const dispatchList: MdtDispatch[] = data.dispatches || [];

        const resourceUrl = sessionEventId
          ? `${API_BASE_URL}/resources?eventId=${encodeURIComponent(sessionEventId)}`
          : `${API_BASE_URL}/resources`;
        const resourceRes = await fetch(resourceUrl, liveFetch);
        if (resourceRes.ok) {
          const resourceData = await resourceRes.json();
          liveResource = (resourceData.resources || []).find((r: Resource) => r.id === mdtResource.id);
          if (!liveResource && sessionEventId) {
            const allRes = await fetch(`${API_BASE_URL}/resources`, liveFetch);
            if (allRes.ok) {
              const allData = await allRes.json();
              liveResource = (allData.resources || []).find((r: Resource) => r.id === mdtResource.id);
            }
          }
          if (liveResource) {
            resourceAssignedIncident = liveResource.assignedIncident ?? null;
            try {
              localStorage.setItem('c2_mdt_resource', JSON.stringify({
                ...mdtResource,
                ...liveResource,
                eventId: mdtResource.eventId || liveResource.eventId,
              }));
            } catch { /* ignore */ }
          }
        }

        processList(dispatchList, resourceAssignedIncident);

        const scopedDispatches = filterForSession(dispatchList);
        const hasPendingDispatch = scopedDispatches.some((d) => d.status === 'pending');

        if (liveResource && !liveResource.assignedIncident && !hasPendingDispatch) {
          dispatch(clearActiveDispatch());
          setUnitStatus('available');
        }

        const activelyAssigned =
          liveResource?.assignedIncident &&
          liveResource.status !== 'available';
        if (
          !hasPendingDispatch &&
          activelyAssigned &&
          !dispatchListCoversIncident(scopedDispatches, liveResource!.assignedIncident!)
        ) {
          const restored = await buildDispatchFromResourceAssignment(
            liveResource,
            liveResource.assignedIncident,
            token
          );
          if (restored) {
            processList([restored], resourceAssignedIncident);
          }
        }

        if (sessionEventId) {
          const eventRes = await fetch(`${API_BASE_URL}/events/${encodeURIComponent(sessionEventId)}`, liveFetch);
          if (eventRes.ok) {
            const eventData = await eventRes.json();
            if (eventData.event?.status === 'closed') {
              logoutMdt(dispatch, navigate, token);
            }
          }
        }
      } catch { /* ignore */ }
    };

    poll();
    const interval = setInterval(poll, 3000);

    const onVisible = () => {
      if (document.visibilityState === 'visible') poll();
    };
    const onAppResumed = () => {
      poll();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('mdt:app-resumed', onAppResumed);

    const socket = io(SOCKET_URL, { transports: ['websocket', 'polling'], auth: { token } });
    socket.on('mdt:dispatch_sent', (d: MdtDispatch) => {
      if (d.targetUnitId !== mdtResource.id) return;
      if (sessionEventId && d.eventId && d.eventId !== sessionEventId) return;
      processList([d]);
    });
    socket.on('mdt:dispatch_cancelled', (payload: { id: string; targetUnitId?: string }) => {
      if (payload.targetUnitId && payload.targetUnitId !== mdtResource.id) return;
      dispatch(cancelDispatch(payload.id));
      dispatch(clearActiveDispatch());
    });
    socket.on('mdt:dispatch_response', (payload: MdtDispatch & { targetUnitId?: string }) => {
      if (payload.targetUnitId && payload.targetUnitId !== mdtResource.id) return;
      if (payload.status === 'completed' || payload.status === 'cancelled') {
        dispatch(cancelDispatch(payload.id));
        dispatch(clearActiveDispatch());
      } else if (payload.status === 'accepted' && payload.respondedAt && payload.incidentId) {
        processList([payload]);
      }
    });
    socket.on('resource:updated', (resource: Resource) => {
      if (resource.id !== mdtResource.id) return;
      const sessionEventId = getMdtEventId(mdtResource);
      if (sessionEventId && resource.eventId && resource.eventId !== sessionEventId) return;
      if (!resource.assignedIncident) {
        dispatch(clearActiveDispatch());
        setUnitStatus('available');
        try {
          localStorage.setItem('c2_mdt_resource', JSON.stringify({
            ...mdtResource,
            ...resource,
            eventId: mdtResource.eventId || resource.eventId,
          }));
        } catch { /* ignore */ }
      }
    });
    socket.on('mdt:event_closed', (payload: { eventId: string; reason?: string }) => {
      const sessionEventId = getMdtEventId(mdtResource);
      if (!payload?.eventId || payload.eventId !== sessionEventId) return;
      logoutMdt(dispatch, navigate, token);
    });
    socket.on(
      'mdt:force_disconnect',
      (payload: { resourceId: string; eventId?: string; userId?: string; reason?: string }) => {
        if (payload.resourceId !== mdtResource.id) return;
        const sessionEventId = getMdtEventId(mdtResource);
        if (sessionEventId && payload.eventId && payload.eventId !== sessionEventId) return;
        logoutMdt(dispatch, navigate, token);
      }
    );
    socket.on(
      'mdt:command_status',
      (payload: { resourceId?: string; eventId?: string; status?: string }) => {
        if (!payload?.status) return;
        if (payload.resourceId && payload.resourceId !== mdtResource.id && payload.resourceId !== mdtResource.name) {
          return;
        }
        const sessionEventId = getMdtEventId(mdtResource);
        if (sessionEventId && payload.eventId && payload.eventId !== sessionEventId) return;
        if (payload.status === 'onscene' || payload.status === 'on_scene') {
          setUnitStatus('onscene');
        } else if (payload.status === 'available') {
          setUnitStatus('available');
          dispatch(clearActiveDispatch());
        }
      }
    );

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('mdt:app-resumed', onAppResumed);
      socket.disconnect();
    };
  }, [sessionReady, mdtResource?.id, token, dispatch, navigate, setUnitStatus]);

  const handleTabChange = (tab: typeof tabs[number]) => {
    if (tab.id === 'chat') {
      setChatOpen(true);
      return;
    }
    setChatOpen(false);
    dispatch(setMobileTab(tab.id as 'dispatch' | 'incidents' | 'map' | 'profile'));
    navigate(tab.path);
  };

  const { isOnline, pendingCount, isSyncing, syncAll } = useOfflineSync();

  return (
    <MobileContext.Provider value={{ unitStatus, setUnitStatus, chatOpen, setChatOpen, mdtResource }}>
      <div className="mobile-dispatch-layout">
        <div className={cn('ambient-bg', `status-${unitStatus}`)} />
        <div className="ambient-glow" style={{ background: statusGlow[unitStatus] }} />

        <header className="relative z-10 flex items-center justify-between px-4 h-11 flex-shrink-0"
          style={{ background: 'rgba(20,26,40,0.5)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(255,255,255,0.04)' }}
        >
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md flex items-center justify-center"
              style={{ background: 'rgba(102,148,255,0.35)', border: '1px solid rgba(102,148,255,0.2)' }}
            >
              <span className="text-[8px] font-black text-white">C2</span>
            </div>
            <span className="text-xs font-bold text-white/90">MDT</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm font-mono font-semibold text-cyan-300">
              {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
            </span>
            <MdtMenu onLogout={handleLogoutConfirm} loggingOut={loggingOut} />
          </div>
        </header>

        <OfflineBanner
          isOnline={isOnline}
          pendingCount={pendingCount}
          isSyncing={isSyncing}
          onSyncNow={syncAll}
        />

        <main className="relative z-10 flex-1 overflow-auto min-h-0">{children}</main>

        <MdtDispatchAlert />

        <nav className="relative z-10 flex items-center py-1.5 pb-3 flex-shrink-0"
          style={{ background: 'rgba(20,26,40,0.6)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', borderTop: '1px solid rgba(255,255,255,0.04)' }}
        >
          {tabs.map((tab) => {
            const isActive =
              tab.id === 'chat'
                ? chatOpen
                : tab.path === '/'
                ? location.pathname === '/' && !chatOpen
                : location.pathname.startsWith(tab.path) && !chatOpen;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab)}
                className={cn(
                  'flex-1 flex flex-col items-center gap-1 py-1 transition-colors',
                  isActive ? 'text-blue-400' : 'text-white/40'
                )}
              >
                <tab.icon className="w-[19px] h-[19px]" />
                <span className="text-[8px] font-semibold">{tab.label}</span>
                {isActive && <div className="w-1 h-1 rounded-full bg-blue-400" />}
              </button>
            );
          })}
        </nav>
      </div>
    </MobileContext.Provider>
  );
}
